import { ReactElement, useState } from "react";
import { Typography, Button, Box, Divider } from "@mui/material";

export interface DoubletDetectionFormProps {
  onClose?: () => void;
  onSubmit?: (data: DoubletDetectionData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

export interface DoubletDetectionData {
  skip?: boolean;
}

const DoubletDetectionForm = ({
  onClose,
  onSubmit,
  onBack,
  onContinue,
  className = "",
}: DoubletDetectionFormProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = () => {
    setIsLoading(true);
    onSubmit?.({});
  };

  return (
    <div
      className={`p-6 h-full flex flex-col justify-between ${className}`}
      style={{
        backgroundColor: "#F5F5F5", // tokens.colors.gray[100]
      }}
    >
      {/* Description */}
      <Box>
        <Typography
          variant="body2"
          className="text-gray-600 mb-4 leading-relaxed"
          sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
        >
          This step will run a doublet detection algorithm and append two
          metrics:
          <code className="bg-pink-100 px-1 rounded mx-1">
            doublet_score
          </code>{" "}
          and
          <code className="bg-pink-100 px-1 rounded mx-1">
            predicted_doublet
          </code>
          . These help identify likely doublets, either for direct filtering or
          downstream inspection.
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          className="mb-6 leading-relaxed"
          sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
        >
          <strong>Note:</strong> This step is optional. You can skip it and
          proceed directly to normalization if you prefer not to detect doublets
          at this stage.
        </Typography>
      </Box>

      <Divider className="mb-6" />

      {/* Action Buttons */}
      <div className="flex justify-between mt-6">
        {onBack && (
          <Button
            variant="outlined"
            color="primary"
            onClick={onBack}
            disabled={isLoading}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1.5,
            }}
          >
            Back
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading}
          className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          {isLoading ? "Running..." : "Run Doublet Detection"}
        </Button>
      </div>
    </div>
  );
};

export default DoubletDetectionForm;
